import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, Users, CalendarCheck, Settings2, Flag, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { oapi } from '@/lib/officialsApi';
import { OfficialsHeader, OfficialsLoader } from '@/components/officials/OfficialsHeader';
import { OfficialsUpsell } from '@/components/officials/OfficialsUpsell';
import { GamesTab } from '@/components/officials/GamesTab';
import { OfficialsDatabaseTab } from '@/components/officials/OfficialsDatabaseTab';
import { AvailabilityTab } from '@/components/officials/AvailabilityTab';
import { SettingsTab } from '@/components/officials/SettingsTab';
import { PaymentsTab } from '@/components/officials/payments/PaymentsTab';

const TABS = [
  { key: 'games', label: 'Games', icon: CalendarDays },
  { key: 'officials', label: 'Officials', icon: Users, manage: true },
  { key: 'availability', label: 'Availability', icon: CalendarCheck },
  { key: 'payments', label: 'Payments', icon: Wallet, manage: true, pay: true },
  { key: 'settings', label: 'Settings', icon: Settings2, manage: true },
];
const ROLE_LABEL = { owner: 'Owner', scheduler: 'Scheduler', collaborator: 'Schedule editor' };
const RETURN_KEYS = ['addon', 'subscription_id', 'ba_token', 'token'];

// Handles the PayPal return (?addon=success|cancelled) then loads the officials context.
function useOfficialsContext() {
  const { isAuthenticated, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [ctx, setCtx] = useState(null);
  const [activating, setActivating] = useState(params.get('addon') === 'success');
  const started = useRef(false);
  const loadCtx = useCallback(() => oapi('/officials/context').then(setCtx).catch(e => toast.error(e.message)), []);

  useEffect(() => {
    if (loading || started.current) return;
    if (!isAuthenticated) { navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`, { replace: true }); return; }
    started.current = true;
    const st = params.get('addon');
    const clean = new URLSearchParams(params);
    RETURN_KEYS.forEach(k => clean.delete(k));
    const finish = () => { setActivating(false); if (st) setParams(clean, { replace: true }); loadCtx(); };
    if (st === 'success' && params.get('subscription_id')) {
      oapi('/officials/billing/capture', { method: 'POST', body: { subscription_id: params.get('subscription_id') } })
        .then(() => { toast.success('Officials Scheduling is active. Welcome aboard!'); refreshUser(); })
        .catch(e => toast.error(e.message))
        .finally(finish);
      return;
    }
    if (st === 'cancelled') toast('Checkout cancelled. You were not charged.');
    finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuthenticated]);

  return { ctx, activating, params, setParams };
}

const HubTabs = ({ tabs, tab, onTab, count }) => (
  <nav className="bg-white border-b sticky top-16 z-40">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
      {tabs.map(t => (
        <button key={t.key} type="button" onClick={() => onTab(t.key)} data-testid={`hub-tab-${t.key}`}
          className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? 'border-[#00247D] text-[#00247D]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <t.icon className="w-4 h-4 hidden sm:block" />{t.label}
          {t.key === 'officials' && count > 0 && <span className="ml-0.5 text-[10px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">{count}</span>}
        </button>
      ))}
    </div>
  </nav>
);

export default function OfficialsHub() {
  const { ctx, activating, params, setParams } = useOfficialsContext();
  const [settings, setSettings] = useState(null);
  const [officials, setOfficials] = useState([]);
  const [payPerms, setPayPerms] = useState(null);
  const usable = (ctx?.orgs || []).filter(o => o.addon_active);
  const current = usable.find(o => o.org_id === params.get('org')) || usable[0];
  const orgId = current?.org_id;
  const manage = !!current && current.role !== 'collaborator';

  const loadOfficials = useCallback(() => {
    if (orgId) oapi('/officials/list', { orgId }).then(d => setOfficials(d.officials)).catch(e => toast.error(e.message));
  }, [orgId]);
  useEffect(() => {
    if (!orgId) return;
    setSettings(null);
    setOfficials([]);
    setPayPerms(null);
    loadOfficials();
    oapi('/officials/settings', { orgId }).then(setSettings).catch(e => toast.error(e.message));
    oapi('/officials/payments/access', { orgId }).then(d => setPayPerms(d.perms || [])).catch(() => setPayPerms([]));
  }, [orgId, loadOfficials]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const tabs = TABS.filter(t => (!t.manage || manage) && (!t.pay || (payPerms || []).includes('view')));
  const tab = tabs.some(t => t.key === params.get('tab')) ? params.get('tab') : 'games';
  const focus = params.get('focus');
  const initialEventId = params.get('event') || (focus ? focus.split(':')[0] : '');
  const portalBtn = ctx?.is_official && (
    <Link to="/officials/portal" className="hidden sm:block">
      <Button variant="outline" size="sm" data-testid="hub-portal-btn"><Flag className="w-4 h-4 mr-1.5" />My officiating</Button>
    </Link>
  );

  if (!ctx || activating) return <OfficialsLoader text={activating ? 'Activating your Officials add-on…' : null} />;
  if (!current) {
    return (
      <div className="min-h-screen bg-slate-50">
        <OfficialsHeader title="Officials Scheduling" subtitle="Premium add-on" manualAnchor="org-overview">{portalBtn}</OfficialsHeader>
        <OfficialsUpsell addon={ctx.addon} isOfficial={ctx.is_official} />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50" data-testid="officials-hub">
      <OfficialsHeader title="Officials" subtitle={`${current.name} · ${ROLE_LABEL[current.role] || current.role}`} manualAnchor="org-overview">
        {usable.length > 1 && (
          <Select value={orgId} onValueChange={v => setParam('org', v)}>
            <SelectTrigger className="w-36 sm:w-52 h-9" data-testid="hub-org-select"><SelectValue /></SelectTrigger>
            <SelectContent>{usable.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {portalBtn}
      </OfficialsHeader>
      <HubTabs tabs={tabs} tab={tab} onTab={k => setParam('tab', k)} count={officials.length} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!settings || !payPerms ? <OfficialsLoader /> : (
          <>
            {tab === 'games' && <GamesTab key={orgId} orgId={orgId} roles={settings.settings.roles} officials={officials}
              initialEventId={initialEventId} canCancel={current.role !== 'scheduler'} />}
            {tab === 'officials' && <OfficialsDatabaseTab orgId={orgId} officials={officials} roles={settings.settings.roles}
              joinLink={settings.settings.join_link} reload={loadOfficials} />}
            {tab === 'availability' && <AvailabilityTab orgId={orgId} manage={manage} officials={officials} />}
            {tab === 'payments' && <PaymentsTab key={orgId} orgId={orgId} officials={officials} roles={settings.settings.roles} competitions={settings.competitions} />}
            {tab === 'settings' && <SettingsTab orgId={orgId} role={current.role} data={settings} onSaved={setSettings} />}
          </>
        )}
      </main>
    </div>
  );
}
